import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { MsrcityInquiryDialog } from "../components/Dialog/MsrcityInquiryDialog";
import { msrcityIconMap } from "../data/msrcityIconMap";
import { godrejBarcaMsrService } from "../services/godrejBacraMsrService";

const StampImage = ({ src, alt, className = "", imageWidth = "100%", imageHeight = "100%" }) => (
  <div className={`relative w-full overflow-hidden ${className}`}>
    <svg viewBox="0 0 220 220" width={imageWidth} height={imageHeight} className="w-full h-auto">
      <defs>
        <mask id="stamp-mask" x="0" y="0" width="100%" height="100%">
          <rect width="100%" height="100%" fill="white" />
          {Array.from({ length: 11 }).map((_, i) => (
            <React.Fragment key={i}>
              <circle cx={10 + i * 20} cy={0} r="5" fill="black" />
              <circle cx={10 + i * 20} cy={220} r="5" fill="black" />
              <circle cx={0} cy={10 + i * 20} r="5" fill="black" />
              <circle cx={220} cy={10 + i * 20} r="5" fill="black" />
            </React.Fragment>
          ))}
        </mask>
      </defs>
      <image
        href={src}
        alt={alt}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        mask="url(#stamp-mask)"
      />
    </svg>
  </div>
);

export default function GodrejBarcaMSR() {
  const [godrejBarcaMsrContent, setGodrejBarcaMsrContent] = useState(null);
  const [isBrochureDialogOpen, setIsBrochureDialogOpen] = useState(false);
  const [isScheduleVisitDialogOpen, setIsScheduleVisitDialogOpen] = useState(false);

  const fadeInVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      const content = await godrejBarcaMsrService.getGodrejContent();
      setGodrejBarcaMsrContent(content);
    };
    fetchContent();
  }, []);

  const handleInquiry = (inquiryType) => {
    if (inquiryType === 'brochure') {
      setIsBrochureDialogOpen(true);
    } else if (inquiryType === 'scheduleVisit') {
      setIsScheduleVisitDialogOpen(true);
    }
  };

  if (!godrejBarcaMsrContent) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
          </div>
    );
  }

  const getIconForText = (text) => {
    const exactMatch = Object.keys(msrcityIconMap).find(key => text.includes(key));
    if (exactMatch) {
      return msrcityIconMap[exactMatch];
    }

    for (const keyword in msrcityIconMap) {
      if (text.includes(keyword)) {
        return msrcityIconMap[keyword];
      }
    }
    return msrcityIconMap["default"];
  };

  const FadeInOnScroll = ({ children, delay = 0, yOffset = 50 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { amount: 0.3 });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: yOffset }}
        animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : yOffset }}
        transition={{ duration: 0.8, delay, ease: "easeOut" }}
      >
        {children}
      </motion.div>
    );
  };
  
  const amenityColors = ['bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-purple-100', 'bg-pink-100', 'bg-teal-100', 'bg-red-100'];

  return (
    <div className="bg-white text-gray-800 font-sans">
      <Helmet>
        <title>{godrejBarcaMsrContent.pageTitle}</title>
      </Helmet>

      <section
        className="relative h-[90vh] flex items-center justify-center text-center px-4 overflow-hidden"
        style={{
          backgroundImage: `url('${godrejBarcaMsrContent.heroSection.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 p-6">
          <motion.h1
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="text-4xl md:text-6xl bg-white text-transparent bg-clip-text font-bold tracking-wide leading-tight drop-shadow-lg font-serif"
          >
                {godrejBarcaMsrContent.heroSection.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            className="text-xl md:text-3xl text-white mt-4 font-bold italic drop-shadow-md"
          >
                {godrejBarcaMsrContent.heroSection.subtitle}

          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
            className="text-lg md:text-2xl text-orange-100 mt-2 drop-shadow-md"
          >
                {godrejBarcaMsrContent.heroSection.tagline}
          </motion.p>
        </div>
      </section>

      <section className="py-16 px-6 bg-accent/10 relative overflow-hidden">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll delay={0.2}>
              <div className="md:order-1">
                <StampImage src={godrejBarcaMsrContent.aboutProject.image} alt="Godrej One, Vikhroli"  />
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={0.4}>
              <div className="md:order-2 text-center md:text-left">
                <h2 className="text-4xl font-extrabold mb-6 text-accent leading-tight">
                  {godrejBarcaMsrContent.aboutProject.heading}
                </h2>
                {godrejBarcaMsrContent.aboutProject.text.map((paragraph, index) => (
                  <motion.p variants={fadeInVariants} key={index} className="text-gray-600 mb-4 leading-relaxed">{paragraph}</motion.p>
                ))}
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-gradient-to-r from-green-50 to-accent/10">
        <div className="max-w-6xl mx-auto">
          <FadeInOnScroll>
            <h2 className="text-4xl font-extrabold mb-12 text-center text-teal-800">
           {godrejBarcaMsrContent.location.heading}
            </h2>
          </FadeInOnScroll>

          {godrejBarcaMsrContent.location.highlights.map((highlight, index) => (
            <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12`}>
              <FadeInOnScroll delay={0.2}>
                <div className={`${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                  <StampImage src={highlight.image} alt={highlight.heading} />
                  <p className="text-sm text-gray-500 mt-2 text-center"></p>
                </div>
              </FadeInOnScroll>
              <FadeInOnScroll delay={0.4}>
                <div className={`${index % 2 !== 0 ? 'md:order-1' : ''}`}>
                  <h3 className="text-4xl font-extrabold text-gray-900 leading-tight mb-2">{highlight.heading}</h3>
                  <p className="text-xl font-bold text-accent leading-tight mb-2">
                    {highlight.title}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    {highlight.text}
                  </p>
                  <ul className="text-left space-y-2">
                    {highlight.points.map((point, pointIndex) => (
                      <motion.li
                        key={pointIndex}
                        className="flex items-center space-x-3 text-gray-700 text-lg"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.5, delay: pointIndex * 0.1 }}
                      >
                        <div className="flex-shrink-0">
                          {getIconForText(point)}
                        </div>
                        <p>{point}</p>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </FadeInOnScroll>
            </div>
          ))}
        </div>
      </section>

      <section className="py-10 px-2 bg-accent ">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <FadeInOnScroll delay={0.2}>
            <div className="order-2 md:order-1">
              <StampImage src={godrejBarcaMsrContent.mediterraneanWay.image} alt="Mediterranean Lifestyle" />

              <p className="text-sm text-gray-500 mt-2 text-center"></p>
            </div>
          </FadeInOnScroll>
          <FadeInOnScroll delay={0.4}>
            <div className="text-center md:text-left order-1 md:order-2">
              <h2 className="text-4xl font-extrabold mb-6 text-white">{godrejBarcaMsrContent.mediterraneanWay.heading}</h2>

              <p className="text-xl text-gray-50 leading-relaxed mb-8">
                {godrejBarcaMsrContent.mediterraneanWay.text}

              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>
    
      <section className="py-16 px-6 bg-white">
  <div className="max-w-6xl mx-auto">
    <FadeInOnScroll>
      <h2 className="text-4xl font-extrabold mb-12 text-center text-rose-800">
        {godrejBarcaMsrContent.amenities.heading}
      </h2>
    </FadeInOnScroll>
    <FadeInOnScroll delay={0.2}>
      <p className="text-xl text-gray-700 leading-relaxed mb-12 text-center">
        {godrejBarcaMsrContent.amenities.description}
      </p>
    </FadeInOnScroll>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {godrejBarcaMsrContent.amenities.amenityList.map((amenity, index) => (
        <FadeInOnScroll key={index} delay={0.3 + index * 0.1}>
          <div className={`${amenityColors[index % amenityColors.length]} p-6 rounded-lg shadow-md text-center flex flex-col h-full`}>
            <h3 className="text-2xl font-bold text-black mb-2">{amenity.heading}</h3>
            <StampImage src={amenity.image} alt={amenity.heading} className="my-4" />
            <p className="text-lg text-gray-700 ">
              {amenity.text}
            </p>
          </div>
        </FadeInOnScroll>
      ))}
    </div>

    <div className="mt-12 text-center">
      <FadeInOnScroll>
        <h3 className="text-3xl font-bold text-accent mb-6">{godrejBarcaMsrContent.sitePlan.heading}</h3>
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.2}>
        <p className="text-lg text-gray-700 mb-8">
          {godrejBarcaMsrContent.sitePlan.description}
        </p>
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.4}>
        <StampImage src={godrejBarcaMsrContent.sitePlan.image} alt="Master Layout Plan" className="mx-auto w-full max-w-4xl" />
        <p className="text-sm text-gray-500 mt-2">Map is not to Scale. Developer reserves all rights to add, remove or change location/name of any amenity/specification at their own Discretion.</p>
      </FadeInOnScroll>
      
    </div>
  </div>
</section>

      {/* About the Developer Section */}
      <section className="py-16 px-6 ">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-center md:justify-start gap-8">
          <FadeInOnScroll delay={0.2}>
            <div className="flex-shrink-0 w-48 md:w-64">
              <img 
                src={godrejBarcaMsrContent.aboutDeveloper.logo}
                alt="Godrej Properties Logo" 
                className="w-full h-auto object-contain" 
              />
            </div>
          </FadeInOnScroll>
          <div className="text-center md:text-left flex-grow">
            <FadeInOnScroll>
              <h2 className="text-4xl font-extrabold mb-3 text-gray-900">{godrejBarcaMsrContent.aboutDeveloper.heading}</h2>
              <h3 className="text-2xl font-bold text-accent mb-4">{godrejBarcaMsrContent.aboutDeveloper.subheading}</h3>
            </FadeInOnScroll>
            <FadeInOnScroll delay={0.2}>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {godrejBarcaMsrContent.aboutDeveloper.description}
              </p>
            </FadeInOnScroll>
            <FadeInOnScroll delay={0.4}>
              <p className="text-lg font-bold text-accent">
                {godrejBarcaMsrContent.aboutDeveloper.record}
              </p>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Pre-book Now Section */}
      <section className="py-20 px-6 bg-accent text-white text-center">
        <div className="max-w-4xl mx-auto">
          <FadeInOnScroll>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">{godrejBarcaMsrContent.prebookSection.heading}</h2>
          </FadeInOnScroll>
          <FadeInOnScroll delay={0.2}>
            <p className="text-xl md:text-2xl mb-8">
              {godrejBarcaMsrContent.prebookSection.tagline}
            </p>
          </FadeInOnScroll>
          <FadeInOnScroll delay={0.4}>
            <button 
              onClick={() => handleInquiry('scheduleVisit')}
             className="bg-white text-accent px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
            >
              Schedule a Visit
            </button>
          </FadeInOnScroll>
        </div>
      </section>
 <MsrcityInquiryDialog
        isBrochureDialogOpen={isBrochureDialogOpen}
        setIsBrochureDialogOpen={setIsBrochureDialogOpen}
        isScheduleVisitDialogOpen={isScheduleVisitDialogOpen}
        setIsScheduleVisitDialogOpen={setIsScheduleVisitDialogOpen}
      />
    </div>
  );
}